import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SuggestCorrectionModal from './SuggestCorrectionModal';
import AffiliateOffer from './AffiliateOffer';
import { pickOfferForBill } from './affiliateOffers';
import './BillDetail.css';

const BillDetail = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [lightboxImage, setLightboxImage] = useState('');
  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    // Send the auth token when present so the owner can open their own
    // private uploads (the API 404s private bills for unidentified viewers).
    // Public bills still load with no token, so logged-out viewing is fine.
    const token = localStorage.getItem('token');
    fetch(`/api/vetbills/${billId}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then(response => {
        if (!response.ok) {
          throw new Error('Unable to load bill');
        }
        return response.json();
      })
      .then(data => {
        setBill(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching bill:', err);
        setError('Unable to load this bill.');
        setLoading(false);
      });
  }, [billId]);

  if (loading) {
    return <div className="bill-detail">Loading...</div>;
  }

  if (error) {
    return <div className="bill-detail">{error}</div>;
  }

  const pageUrls = Array.isArray(bill.pageUrls) && bill.pageUrls.length > 0 ? bill.pageUrls : [bill.imageUrl];

  return (
    <div className="bill-detail">
      <div className="bill-inner">
        <button className="back-button" onClick={() => navigate(-1)}>← Back</button>

        <div className="bill-detail-header">
          <div>
            <span className="bill-type-badge">{bill.documentType || 'Upload'}</span>
            {bill.visibility === 'private' && (
              <span className="bill-type-badge bill-private-badge">Private — only you can see this</span>
            )}
            <h2>
              <Link to={`/hospitals/${encodeURIComponent(bill.vetHospital)}`} className="bill-hospital-link">
                {bill.vetHospital || 'Unknown hospital'}
              </Link>
            </h2>
            <p className="bill-meta">
              {[bill.cityRegion, new Date(bill.billDate).toLocaleDateString()].filter(Boolean).join(' · ')}
            </p>
          </div>
          <button
            type="button"
            className="suggest-correction-button"
            onClick={() => setCorrectionTarget({
              targetType: 'bill',
              billId: bill._id,
              hospitalName: bill.vetHospital,
              field: 'Other',
              currentValue: `${bill.vetHospital || 'Unknown hospital'} - ${bill.documentType || 'upload'}`,
              label: 'This uploaded document'
            })}
          >
            Suggest correction
          </button>
        </div>

        <div className="bill-stats-row">
          <div>
            <span>Services</span>
            <strong>{bill.services?.length || 0}</strong>
          </div>
        </div>

        <div className="bill-detail-layout">
          <div className="bill-image-panel">
            <div className="panel-section-header">
              <span>Uploaded document{pageUrls.length > 1 ? ` (${pageUrls.length} pages)` : ''}</span>
            </div>
            <img src={pageUrls[activePage]} alt={`Uploaded vet bill${pageUrls.length > 1 ? `, page ${activePage + 1}` : ''}`} />
            {pageUrls.length > 1 && (
              <div className="page-pager bill-detail-pager">
                <button type="button" onClick={() => setActivePage(p => Math.max(0, p - 1))} disabled={activePage === 0} aria-label="Previous page">‹</button>
                <span>Page {activePage + 1} of {pageUrls.length}</span>
                <button type="button" onClick={() => setActivePage(p => Math.min(pageUrls.length - 1, p + 1))} disabled={activePage === pageUrls.length - 1} aria-label="Next page">›</button>
              </div>
            )}
            <button
              type="button"
              className="view-full-link"
              onClick={() => setLightboxImage(pageUrls[activePage])}
            >
              View full size ↗
            </button>
          </div>

          <div className="bill-services-panel">
            <div className="panel-section-header">
              <span>Extracted services</span>
              {bill.services?.length > 0 && (
                <span className="services-count-badge">{bill.services.length}</span>
              )}
            </div>
            {bill.services?.length ? (
              <table>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th className="price-col">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {bill.services.map(service => {
                    const totalValue = service.totalCost != null
                      ? `$${Number(service.totalCost).toFixed(2)}`
                      : '—';
                    const category = service.standardization?.category;

                    return (
                      <tr key={service._id || service.description}>
                        <td data-label="Service">
                          <span className="service-name">{service.description}</span>
                          {category && category !== 'Other' && (
                            <span className="service-category">{category}</span>
                          )}
                        </td>
                        <td className="price-col" data-label="Total">{totalValue}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="no-services">We couldn’t find any services on this document.</p>
            )}
          </div>
        </div>

        <AffiliateOffer offer={pickOfferForBill(bill)} />

        <SuggestCorrectionModal correctionTarget={correctionTarget} onClose={() => setCorrectionTarget(null)} />
      </div>

      {lightboxImage && (
        <div className="bill-lightbox" onClick={() => setLightboxImage('')}>
          <img src={lightboxImage} alt="Uploaded vet document" onClick={e => e.stopPropagation()} />
          <button className="bill-lightbox-close" onClick={() => setLightboxImage('')}>✕ Close</button>
        </div>
      )}
    </div>
  );
};

export default BillDetail;
